import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import FocusLock from 'react-focus-lock'
import { AnimatePresence, motion } from 'motion/react'
import { Trans } from '@lingui/react/macro'

import type { Customer } from '@jotta/grpc-connect-openapi/customer'
import { useClickOutside } from '@jotta/hooks'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@jotta/ui/Accordion'
import { PlainBrandIcon } from '@jotta/ui/BrandIcon'
import SvgLogoHeader from '@jotta/ui/SvgLogo_headerLight'
import { Breakpoint, useBreakpoint } from '@jotta/ui/useBreakpoint'
import {
  CustomerInfo,
  SubscriptionInfo,
  UserMenuActions,
} from '@jotta/ui/UserMenu'
import { cn } from '@jotta/utils/css'

import { NavBar } from './NavBar'

export function NavigationOverlay({ customer }: { customer: Customer }) {
  const [open, setOpen] = useState(false)
  const [userMenuOpen, setUserMenuOpen] = useState(false)

  const navigationOverlayRef = useRef<HTMLDivElement>(null)
  const userMenuRef = useRef(null)
  const menuButtonRef = useRef<HTMLButtonElement>(null)

  const showMenuTitle = useBreakpoint(Breakpoint.sm)

  useClickOutside(navigationOverlayRef, () => setOpen(false), {
    ignoreRefs: [menuButtonRef],
  })

  useClickOutside(userMenuRef, e => {
    if (userMenuOpen) {
      e.preventDefault()
    }
    setUserMenuOpen(false)
  })

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setOpen(false)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  useEffect(() => {
    if (!open) {
      setUserMenuOpen(false)
      return
    }

    const { overflow } = document.documentElement.style
    document.documentElement.style.overflow = 'hidden'

    return () => {
      document.documentElement.style.overflow = overflow
    }
  }, [open])

  useLayoutEffect(() => {
    if (!open) {
      return
    }

    const currentPageElement = navigationOverlayRef.current?.querySelector(
      '[aria-current="page"]',
    )

    currentPageElement?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }, [open])

  return (
    <FocusLock disabled={!open} returnFocus>
      <button
        ref={menuButtonRef}
        aria-label={open ? 'Close the menu' : 'Open the menu'}
        aria-expanded={open}
        aria-controls="navigation-overlay"
        onClick={() => setOpen(prev => !prev)}
        className={cn('btn', {
          'btn-elevated': !open,
          'btn-tonal z-appNavigationOverlay': open,
        })}
      >
        <HamburgerXIcon open={open} />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            id="navigation-overlay"
            initial="hidden"
            animate="visible"
            exit="exit"
            transition={{ duration: 0.15 }}
            variants={{
              hidden: { opacity: 0 },
              visible: { opacity: 1 },
              exit: { opacity: 0 },
            }}
            role="dialog"
            aria-modal="true"
            className="fixed inset-0 flex flex-row-reverse bg-black/15 backdrop-blur-sm"
          >
            <div
              ref={navigationOverlayRef}
              className="relative flex w-full flex-col bg-on-primary max-sm:inset-0 sm:inset-y-0 sm:right-0 sm:max-w-sm sm:rounded-l-xl"
            >
              <div className="flex items-center p-4">
                <h2 className="text-center text-xl font-semibold leading-10">
                  {showMenuTitle ? (
                    <span className="pl-2">
                      <Trans>Menu</Trans>
                    </span>
                  ) : (
                    <PlainBrandIcon icon={SvgLogoHeader} height={40} />
                  )}
                </h2>
              </div>
              <div className="flex min-h-0 flex-1">
                <div className="flex w-full flex-1 flex-col items-start">
                  <NavBar
                    className="w-full grow overflow-y-scroll pl-2"
                    onNavigate={() => setOpen(false)}
                    onboardingEnabled={false}
                  />

                  {userMenuOpen && (
                    <div className="absolute inset-0 rounded-l-xl bg-surface-container-lowest/50" />
                  )}

                  <FocusLock
                    disabled={!userMenuOpen}
                    returnFocus
                    className="sticky bottom-0 w-full rounded-bl-xl bg-on-primary shadow-[0_-4px_15px_0_theme(colors.primary/6%)]"
                  >
                    <Accordion
                      type="single"
                      collapsible
                      ref={userMenuRef}
                      value={userMenuOpen ? 'open' : 'closed'}
                      onValueChange={value => setUserMenuOpen(value === 'open')}
                    >
                      <AccordionItem value="open" className="border-none">
                        <AccordionTrigger className="px-4 [&>div>svg]:btn [&>div>svg]:btn-tonal hover:no-underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-m3-primary">
                          <CustomerInfo
                            customer={customer}
                            className="rounded-none p-0"
                          />
                        </AccordionTrigger>
                        <AccordionContent className="p-0">
                          <SubscriptionInfo
                            customer={customer}
                            className="rounded-none border-y-2 border-surface-container-low"
                          />
                          <UserMenuActions
                            onClick={() => setOpen(false)}
                            className="p-3 [&>button]:btn-tonal"
                          />
                        </AccordionContent>
                      </AccordionItem>
                    </Accordion>
                  </FocusLock>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </FocusLock>
  )
}

function HamburgerXIcon({ open }: { open: boolean }) {
  const transition = { duration: 0.2 }
  return (
    <motion.svg
      viewBox="0 0 24 24"
      width="20"
      height="20"
      aria-hidden="true"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    >
      <motion.g
        animate={open ? { rotate: 45, y: 5 } : { rotate: 0, y: 0 }}
        transition={transition}
        style={{ originX: 0.5, originY: 0.5 }}
      >
        <line x1="2" y1="7" x2="22" y2="7" />
      </motion.g>

      <motion.g
        animate={open ? { opacity: 0 } : { opacity: 1 }}
        transition={transition}
      >
        <line x1="2" y1="12" x2="22" y2="12" />
      </motion.g>

      <motion.g
        animate={open ? { rotate: -45, y: -5 } : { rotate: 0, y: 0 }}
        transition={transition}
        style={{ originX: 0.5, originY: 0.5 }}
      >
        <line x1="2" y1="17" x2="22" y2="17" />
      </motion.g>
    </motion.svg>
  )
}
